import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Edit, Trash2, Eye, BarChart3, Upload, Save, X } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';
import AdminNav from '../components/AdminNav';

const RewardManagerPage = () => {
  const navigate = useNavigate();
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [previewReward, setPreviewReward] = useState(null);
  
  // Filter states
  const [filterMonth, setFilterMonth] = useState('all');
  const [filterActive, setFilterActive] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  
  const [formData, setFormData] = useState({
    name: '',
    reward_type: 'sticker',
    cost: 5,
    description: '',
    available_start: '',
    available_end: '',
    icon: '🎨',
    month: 'January',
    image: null
  });
  const { toast } = useToast();

  useEffect(() => {
    loadData();
  }, [filterMonth, filterActive, sortBy]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Set admin API token
      const adminToken = localStorage.getItem('admin_token');
      if (!adminToken) {
        toast({
          title: 'Authentication Error',
          description: 'Please log in as admin',
          variant: 'destructive'
        });
        navigate('/admin/login');
        return;
      }
      
      // Build query params
      const params = new URLSearchParams();
      if (filterMonth && filterMonth !== 'all') params.append('month', filterMonth);
      if (filterActive && filterActive !== 'all') {
        // Convert string 'true'/'false' to actual boolean for backend
        params.append('active', filterActive === 'true' ? 'true' : 'false');
      }
      if (sortBy) params.append('sort_by', sortBy);
      
      const [rewardsData, statsData] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rewards/items?${params}`, {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }).then(r => r.json()),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/rewards/stats`, {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }).then(r => r.json())
      ]);
      
      setRewards(rewardsData);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load rewards data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReward = async (e) => {
    e.preventDefault();
    
    if (!formData.image) {
      toast({
        title: 'Error',
        description: 'Please select an image',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      const adminToken = localStorage.getItem('admin_token');
      const formDataToSend = new FormData();
      
      formDataToSend.append('name', formData.name);
      formDataToSend.append('reward_type', formData.reward_type);
      formDataToSend.append('cost', formData.cost.toString());
      formDataToSend.append('description', formData.description);
      formDataToSend.append('available_start', formData.available_start);
      formDataToSend.append('available_end', formData.available_end);
      formDataToSend.append('icon', formData.icon);
      formDataToSend.append('image', formData.image);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/rewards`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        },
        body: formDataToSend
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create reward');
      }
      
      toast({
        title: 'Success!',
        description: 'Reward created successfully',
      });
      
      setIsCreateDialogOpen(false);
      resetForm();
      loadData();
      
    } catch (error) {
      console.error('Error creating reward:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create reward',
        variant: 'destructive'
      });
    }
  };

  const handleEditReward = async (e) => {
    e.preventDefault();
    
    try {
      const adminToken = localStorage.getItem('admin_token');
      const formDataToSend = new FormData();
      
      formDataToSend.append('name', formData.name);
      formDataToSend.append('reward_type', formData.reward_type);
      formDataToSend.append('cost', formData.cost.toString());
      formDataToSend.append('description', formData.description);
      formDataToSend.append('available_start', formData.available_start);
      formDataToSend.append('available_end', formData.available_end);
      formDataToSend.append('icon', formData.icon);
      
      if (formData.image) {
        formDataToSend.append('image', formData.image);
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/rewards/${editingReward.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        },
        body: formDataToSend
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update reward');
      }
      
      toast({
        title: 'Success!',
        description: 'Reward updated successfully',
      });
      
      setIsEditDialogOpen(false);
      setEditingReward(null);
      resetForm();
      loadData();
      
    } catch (error) {
      console.error('Error updating reward:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update reward',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteReward = async (rewardId) => {
    if (!window.confirm('Are you sure you want to delete this reward? This action cannot be undone.')) {
      return;
    }
    
    try {
      const adminToken = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/rewards/${rewardId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete reward');
      }
      
      toast({
        title: 'Success!',
        description: 'Reward deleted successfully',
      });
      
      loadData();
      
    } catch (error) {
      console.error('Error deleting reward:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete reward',
        variant: 'destructive'
      });
    }
  };

  const openEditDialog = (reward) => {
    setEditingReward(reward);
    setFormData({
      name: reward.name,
      reward_type: reward.reward_type,
      cost: reward.cost,
      description: reward.description || '',
      available_start: reward.available_start ? new Date(reward.available_start).toISOString().slice(0, 16) : '',
      available_end: reward.available_end ? new Date(reward.available_end).toISOString().slice(0, 16) : '',
      icon: reward.icon || '🎨',
      image: null
    });
    setIsEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      reward_type: 'sticker',
      cost: 5,
      description: '',
      available_start: '',
      available_end: '',
      icon: '🎨',
      image: null
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Error',
          description: 'Please select an image file',
          variant: 'destructive'
        });
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast({
          title: 'Error',
          description: 'Image must be smaller than 5MB',
          variant: 'destructive'
        });
        return;
      }
      
      setFormData(prev => ({ ...prev, image: file }));
    }
  };

  const stickers = rewards.filter(r => r.reward_type === 'sticker');
  const pets = rewards.filter(r => r.reward_type === 'pet');

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-xl text-slate-600">Loading reward manager...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminNav />
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Reward Manager</h1>
            <p className="text-slate-600 mt-2">Create and manage stickers, pets, and rewards</p>
          </div>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-green-600 hover:bg-green-700">
                <Plus className="w-4 h-4 mr-2" />
                Add New Reward
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <form onSubmit={handleCreateReward}>
                <DialogHeader>
                  <DialogTitle>Create New Reward</DialogTitle>
                  <DialogDescription>
                    Add a new sticker or pet to the reward store
                  </DialogDescription>
                </DialogHeader>

                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Reward name"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="reward_type">Type *</Label>
                      <Select value={formData.reward_type} onValueChange={(value) => setFormData(prev => ({ ...prev, reward_type: value, icon: value === 'sticker' ? '🎨' : '🐾' }))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sticker">🎨 Sticker</SelectItem>
                          <SelectItem value="pet">🐾 Pet</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="cost">Token Cost *</Label>
                      <Input
                        id="cost"
                        type="number"
                        min="1"
                        value={formData.cost}
                        onChange={(e) => setFormData(prev => ({ ...prev, cost: parseInt(e.target.value) || 1 }))}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="icon">Icon</Label>
                      <Input
                        id="icon"
                        value={formData.icon}
                        onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value }))}
                        placeholder="🎨"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Optional description"
                      rows={3}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="available_start">Available From *</Label>
                      <Input
                        id="available_start"
                        type="datetime-local"
                        value={formData.available_start}
                        onChange={(e) => setFormData(prev => ({ ...prev, available_start: e.target.value }))}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="available_end">Available Until *</Label>
                      <Input
                        id="available_end"
                        type="datetime-local"
                        value={formData.available_end}
                        onChange={(e) => setFormData(prev => ({ ...prev, available_end: e.target.value }))}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="image">Image *</Label>
                    <Input
                      id="image"
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="cursor-pointer"
                      required
                    />
                    <p className="text-sm text-slate-500 mt-1">PNG, JPG, or JPEG. Max 5MB.</p>
                  </div>
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => { setIsCreateDialogOpen(false); resetForm(); }}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-green-600 hover:bg-green-700">
                    <Save className="w-4 h-4 mr-2" />
                    Create Reward
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Rewards</CardTitle>
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totals.rewards}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Stickers</CardTitle>
                <div className="text-lg">🎨</div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totals.stickers}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Pets</CardTitle>
                <div className="text-lg">🐾</div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totals.pets}</div>
              </CardContent>
            </Card>
            <Card 
              className="cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => navigate('/admin/purchases')}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Purchases</CardTitle>
                <div className="text-lg">🛒</div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totals.purchases}</div>
                <p className="text-xs text-muted-foreground mt-1">Click to view & manage</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Filters & Sorting</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label>Month</Label>
                <Select value={filterMonth} onValueChange={setFilterMonth}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Months" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Months</SelectItem>
                    <SelectItem value="January">January</SelectItem>
                    <SelectItem value="February">February</SelectItem>
                    <SelectItem value="March">March</SelectItem>
                    <SelectItem value="April">April</SelectItem>
                    <SelectItem value="May">May</SelectItem>
                    <SelectItem value="June">June</SelectItem>
                    <SelectItem value="July">July</SelectItem>
                    <SelectItem value="August">August</SelectItem>
                    <SelectItem value="September">September</SelectItem>
                    <SelectItem value="October">October</SelectItem>
                    <SelectItem value="November">November</SelectItem>
                    <SelectItem value="December">December</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Status</Label>
                <Select value={filterActive} onValueChange={setFilterActive}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="true">Active</SelectItem>
                    <SelectItem value="false">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Sort By</Label>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name">Name (A-Z)</SelectItem>
                    <SelectItem value="cost">Cost</SelectItem>
                    <SelectItem value="month">Month</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-end">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setFilterMonth('all');
                    setFilterActive('all');
                    setSortBy('name');
                  }}
                  className="w-full"
                >
                  Clear Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Rewards Grid */}
        <div className="space-y-6">
          {/* Stickers Section */}
          <div>
            <h2 className="text-xl font-bold mb-4">Stickers ({stickers.length})</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {stickers.map((reward) => (
                <Card key={reward.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-lg overflow-hidden border-2 border-slate-200 flex-shrink-0 bg-white">
                        <img 
                          src={`${process.env.REACT_APP_BACKEND_URL}${reward.image_url}`}
                          alt={reward.name}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm truncate">{reward.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {reward.cost} tokens
                          </Badge>
                          <span className="text-lg">{reward.icon}</span>
                        </div>
                      </div>
                    </div>
                    
                    {reward.description && (
                      <p className="text-xs text-slate-600 mb-3 line-clamp-2">{reward.description}</p>
                    )}
                    
                    <div className="flex items-center justify-between">
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => openEditDialog(reward)}>
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleDeleteReward(reward.id)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                      <Badge variant={new Date() >= new Date(reward.available_start) && new Date() <= new Date(reward.available_end) ? "default" : "secondary"} className="text-xs">
                        {new Date() >= new Date(reward.available_start) && new Date() <= new Date(reward.available_end) ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Pets Section */}
          <div>
            <h2 className="text-xl font-bold mb-4">Pets ({pets.length})</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {pets.map((reward) => (
                <Card key={reward.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-lg overflow-hidden border-2 border-slate-200 flex-shrink-0 bg-white">
                        <img 
                          src={`${process.env.REACT_APP_BACKEND_URL}${reward.image_url}`}
                          alt={reward.name}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm truncate">{reward.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {reward.cost} tokens
                          </Badge>
                          <span className="text-lg">{reward.icon}</span>
                        </div>
                      </div>
                    </div>
                    
                    {reward.description && (
                      <p className="text-xs text-slate-600 mb-3 line-clamp-2">{reward.description}</p>
                    )}
                    
                    <div className="flex items-center justify-between">
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => openEditDialog(reward)}>
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleDeleteReward(reward.id)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                      <Badge variant={new Date() >= new Date(reward.available_start) && new Date() <= new Date(reward.available_end) ? "default" : "secondary"} className="text-xs">
                        {new Date() >= new Date(reward.available_start) && new Date() <= new Date(reward.available_end) ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleEditReward}>
              <DialogHeader>
                <DialogTitle>Edit Reward</DialogTitle>
                <DialogDescription>
                  Update reward details
                </DialogDescription>
              </DialogHeader>

              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-name">Name *</Label>
                    <Input
                      id="edit-name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Reward name"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-reward_type">Type *</Label>
                    <Select value={formData.reward_type} onValueChange={(value) => setFormData(prev => ({ ...prev, reward_type: value, icon: value === 'sticker' ? '🎨' : '🐾' }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sticker">🎨 Sticker</SelectItem>
                        <SelectItem value="pet">🐾 Pet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-cost">Token Cost *</Label>
                    <Input
                      id="edit-cost"
                      type="number"
                      min="1"
                      value={formData.cost}
                      onChange={(e) => setFormData(prev => ({ ...prev, cost: parseInt(e.target.value) || 1 }))}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-icon">Icon</Label>
                    <Input
                      id="edit-icon"
                      value={formData.icon}
                      onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value }))}
                      placeholder="🎨"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="edit-description">Description</Label>
                  <Textarea
                    id="edit-description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Optional description"
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-available_start">Available From *</Label>
                    <Input
                      id="edit-available_start"
                      type="datetime-local"
                      value={formData.available_start}
                      onChange={(e) => setFormData(prev => ({ ...prev, available_start: e.target.value }))}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-available_end">Available Until *</Label>
                    <Input
                      id="edit-available_end"
                      type="datetime-local"
                      value={formData.available_end}
                      onChange={(e) => setFormData(prev => ({ ...prev, available_end: e.target.value }))}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="edit-image">New Image (optional)</Label>
                  <Input
                    id="edit-image"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="cursor-pointer"
                  />
                  <p className="text-sm text-slate-500 mt-1">Leave empty to keep current image. PNG, JPG, or JPEG. Max 5MB.</p>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setIsEditDialogOpen(false); setEditingReward(null); resetForm(); }}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  <Save className="w-4 h-4 mr-2" />
                  Update Reward
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default RewardManagerPage;